import React, { Dispatch, SetStateAction } from 'react';
import {
  View, Pressable, StyleSheet, Text, FlexStyle, ScrollView,
  Platform,
} from 'react-native';
import { FontAwesome } from '@expo/vector-icons';
import Style from '../constants/Style';
import Colors from '../constants/Colors';
import { PressableState } from './PressableStyled';
import CheckBox from './16pxIcons/CheckBox';
import RadioButton from './RadioButton';

export type DropdownMenuOption = {
  name?: string,
  Name?: string,
  testID?: string,
  [key: string]: any,
}

interface DropdownMenuProps {
  expanded: boolean,
  setExpanded: Dispatch<SetStateAction<boolean>>,
  options: DropdownMenuOption[],
  selectedOption: DropdownMenuOption | DropdownMenuOption[],
  setSelectedOption: Dispatch<SetStateAction<DropdownMenuOption | DropdownMenuOption[]>>,
  title?: string,
  style?: FlexStyle | FlexStyle[],
  label?: string,
  defaultTitle?: string,
  firstOptionTitle?: string,
  multiSelect?: boolean,
  hideSelectAll?: boolean,
  radioButtonMode?: boolean,
  buttonTestID?: string,
  listTestID?: string,
  top?: boolean,
}

const DropdownMenu = ({
  expanded, setExpanded, options, setSelectedOption, selectedOption, style, title, label,
  multiSelect, defaultTitle, firstOptionTitle, hideSelectAll, radioButtonMode, buttonTestID,
  listTestID, top,
}: DropdownMenuProps) => <View style={[styles.wrapper, style]}>
    {!!label && <Text style={styles.label}>{label}</Text>}
    {radioButtonMode ? <View style={styles.radioButtonContent}>
      {options.map((option: DropdownMenuOption) => <RadioButton
        select={() => setSelectedOption(option as DropdownMenuOption)}
        selected={selectedOption as DropdownMenuOption === option as DropdownMenuOption}
        title={option?.name ?? option?.Name}
        key={`radio_button_${option?.name ?? option?.Name}`}
      />)}
    </View> : <>
      <Pressable
        style={styles.container}
        onPress={() => setExpanded(!expanded)}
        testID={buttonTestID}
      >
        <Text style={Style.dropdownText}>
          {!!selectedOption && (multiSelect ? defaultTitle
            : ((selectedOption as DropdownMenuOption).name
              ?? (selectedOption as DropdownMenuOption).Name))}
          {!selectedOption && (defaultTitle
            ?? (title ? `Select a ${title.toLowerCase()}` : 'Select an option'))}
        </Text>
        <FontAwesome
          name={expanded ? 'angle-up' : 'angle-down'}
          size={20}
          color={Colors.light.hereworksBlue500}
        />
      </Pressable>
      <View>
        {expanded && <ScrollView
          style={[Style.dropdownMenu, styles.list, top && { bottom: 40 }]}
          nestedScrollEnabled
          testID={listTestID}
        >
          {(hideSelectAll ? options : [null, ...options])
            .map((option: DropdownMenuOption, index: number) => {
              let selected: boolean = false;
              if (multiSelect) {
                selected = option ? (selectedOption as DropdownMenuOption[] ?? []).includes(option)
                  : (selectedOption as DropdownMenuOption[] ?? []).length === options.length;
              } else selected = selectedOption === option;
              const highlighted: boolean = selected && !multiSelect;

              return <Pressable
                onPress={() => {
                  if (multiSelect) {
                    if (option) {
                      setSelectedOption(selected
                        ? (selectedOption as DropdownMenuOption[])
                          .filter((o: DropdownMenuOption) => o !== option)
                        : [...(selectedOption as DropdownMenuOption[]), option]);
                    } else setSelectedOption(selected ? [] : options);
                  } else {
                    setSelectedOption(option);
                    setExpanded(false);
                  }
                }}
                style={({ hovered, pressed }: PressableState) => [
                  hovered && Style.listItemHovered, pressed && Style.listItemPressed,
                  Style.dropdownOption, Style.row, highlighted && Style.listItemSelected,
                ]}
                key={`option_${option}_${index}`}
                testID={option?.testID}
              >
                {multiSelect && <CheckBox
                  style={styles.checkbox}
                  unselected={!selected}
                />}
                <Text style={[Style.dropdownText, highlighted && Style.selectedText, { flex: 1 }]}>
                  {option ? (option.name ?? option.Name)
                    : (firstOptionTitle ?? defaultTitle ?? `All ${title?.toLowerCase()}s`)}
                </Text>
              </Pressable>;
            })}
        </ScrollView>}
      </View>
    </>}
  </View>;

const styles = StyleSheet.create({
  wrapper: {
    marginBottom: 8,
  },
  label: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    marginBottom: 4,
  },
  container: {
    ...Style.dropdownMenu,
    ...Style.dropdownOption,
    ...Style.row,
    position: 'relative',
    flex: 1,
    maxHeight: 40,
  },
  list: {
    marginTop: 4,
    position: Platform.OS === 'android' ? 'relative' : 'absolute',
  },
  checkbox: {
    marginEnd: 8,
  },
  radioButtonContent: {
    ...Style.row,
    justifyContent: 'flex-start',
  },
});

export default DropdownMenu;
